import { QueryClient } from "@tanstack/react-query";
import { ConversationClient, OperationClient } from "../../../../api/api";
import { createScopedLogger } from "../../../../common/logger";
import { VariableNames } from "../../../../UserInterfaceModule/Constants/VariableNames";
import { ComponentService } from "../../../../UserInterfaceModule/Services/ComponentService";
import { IsDesignMode } from "../../../../UserInterfaceModule/Utils/runtimeDetection";
import { DomainServiceOperationCallExpression } from "../../../Models/DomainServices/DomainServiceOperationCallExpression";
import { IExpressionEvaluator, IHasExpressionMetadata } from "../../../Models/IExpressionEvaluator";
import { IExpressionEvaluatorContext } from "../../../Models/IExpressionEvaluatorContext";
import { MaybePromise } from "../../../Models/MaybePromise";
import { BundleServices, BundleServicesKey } from "./DomainServiceEnumItemsExpressionEvaluator";
import { EMPTY_GUID } from "./EMPTY_GUID";

const logger = createScopedLogger("DomainServiceOperationCallExpressionEvaluator");

export type ApiServices = {
  operationClient: OperationClient;
  conversationClient: ConversationClient;
  queryClient: QueryClient;
  errorHandler: (error: any) => void;
};

export const ApiServicesKey = "ApiServices";

export const DOMAIN_SERVICE_OPERATION_QUERY_KEY = "DomainServiceOperation";

export class DomainServiceOperationCallExpressionEvaluator implements IExpressionEvaluator<DomainServiceOperationCallExpression>, IHasExpressionMetadata {
  get type(): string {
    return "DomainService.OperationCall";
  }

  // Only "Request" reads variables from the parent context — it determines the query parameters
  // and should trigger re-evaluation when its referenced variables change.
  // "ResponseTransform" runs in an isolated child context (with $source = API response),
  // so its variable references are scoped to that child and must not trigger parent re-observation.
  // "ResponseVariable" is a write target, never a read input — including it would cause
  // the evaluator to observe the very variable it sets, creating an infinite update loop.
  private static _expressionPropertyNames: string[] = ["Request"];

  get expressionProperties(): string[] {
    return DomainServiceOperationCallExpressionEvaluator._expressionPropertyNames;
  }

  private static DefaultStaleTime = 10000;

  private logTransformed(result: any): any {
    logger.debug("Transformed response:", result);
    return result;
  }

  private applyResponseTransform(response: any, expression: DomainServiceOperationCallExpression, context: IExpressionEvaluatorContext): MaybePromise {
    if (!expression.ResponseTransform) {
      return response;
    }

    const transformContext = context.CreateChild();
    transformContext.VariableScope.set(VariableNames.Source, response);

    const transformed = context.Evaluator.evaluate(expression.ResponseTransform, transformContext);

    if (transformed instanceof Promise) {
      return transformed.then((result) => this.logTransformed(result));
    }

    return this.logTransformed(transformed);
  }

  async evaluate(expression: DomainServiceOperationCallExpression, context: IExpressionEvaluatorContext): Promise<any> {
    const { operationClient, queryClient, errorHandler } = context.GetProperty<ApiServices>(ApiServicesKey);
    const { bundle } = context.GetProperty<BundleServices>(BundleServicesKey);

    // Get the bundle ID
    const definitionId = bundle.Id ?? EMPTY_GUID;
    logger.debug("Retrieved from context - bundle.Id:", bundle.Id, "bundle.CurrentAppName:", bundle.CurrentAppName, "definitionId:", definitionId);

    // Get the component ID from ComponentService if available, otherwise use empty GUID
    let componentId = EMPTY_GUID;
    try {
      const componentService = context.GetProperty<ComponentService>(ComponentService.KEY);
      if (componentService) {
        const viewComponentId = (componentService as any).viewComponentId;
        if (viewComponentId) {
          componentId = viewComponentId;
          logger.debug("Using component ID:", componentId);
        }
      }
    } catch {
      // ComponentService not available in context, use default
      logger.debug("ComponentService not available, using default component ID");
    }

    try {
      const request = await context.Evaluator.evaluate(expression.Request, context);
      const response = await queryClient.fetchQuery({
        queryKey: [DOMAIN_SERVICE_OPERATION_QUERY_KEY, expression.Operation.Id, definitionId, componentId, request],
        queryFn: () => operationClient.execute(expression.Operation.Id, expression.Operation.Name, componentId, definitionId, IsDesignMode, request),
        staleTime: 5000, // 5 seconds - prevents unnecessary refetches when request values haven't changed
      });
      logger.debug("response:", response);

      const responseTransformed = await this.applyResponseTransform(response, expression, context);

      if (expression.ResponseVariable) {
        context.Evaluator.assign(expression.ResponseVariable, context, responseTransformed);
      }

      return responseTransformed;
    } catch (error) {
      errorHandler(error);
      throw error;
    }
  }
}
